import { useEffect, useRef } from 'react';

import * as Sentry from '@sentry/react';

import { useClientStorageItem, useSetClientStorageItem } from '@/entities/clientStorage';
import { useCognitoCheckUser, useCurrentUser } from '@/entities/user/currentUser';

import type { EntrySourceState } from '../lib/types';

import { ENTRY_SOURCE_KEY, SOURCE_RECORDING } from '../lib/constants';
import { isPreCutoffUser } from '../lib/isPreCutoffUser';
import {
  clearEntrySource,
  getLocalEntrySource,
  recordingEntrySourcePersistMatches,
} from '../lib/localStorage';

const LOADING: EntrySourceState = { status: 'loading' };
const RESOLVED_NORMAL: EntrySourceState = { source: 'normal', status: 'resolved' };
const RESOLVED_RECORDING: EntrySourceState = { source: 'recording', status: 'resolved' };

export const useEntrySource = (): EntrySourceState => {
  const cognito = useCognitoCheckUser();
  const isAuthenticated = Boolean(cognito.data?.isAuthorized);

  const userQuery = useCurrentUser();
  const user = userQuery.data;
  const grandfathered = Boolean(user && isPreCutoffUser(user));

  const clientStorageEnabled = isAuthenticated && Boolean(user) && !grandfathered;
  const {
    data: item,
    isError: itemError,
    isLoading: itemLoading,
  } = useClientStorageItem(ENTRY_SOURCE_KEY, { enabled: clientStorageEnabled });

  const setItem = useSetClientStorageItem();
  const syncAttempted = useRef(false);
  const readErrorReported = useRef(false);

  const localFlag = getLocalEntrySource();
  const pendingRecordingSignupPersist = recordingEntrySourcePersistMatches(user?.email);
  const hasLocalRecordingIntent = localFlag === 'recording' || pendingRecordingSignupPersist;

  useEffect(() => {
    if (itemError && !readErrorReported.current) {
      readErrorReported.current = true;

      Sentry.captureException(new Error('[entry-source] clientStorage read failed'), {
        tags: { feature: 'entrySource', phase: 'read' },
      });
    }
  }, [itemError]);

  useEffect(() => {
    if (!clientStorageEnabled) return;

    if (itemLoading || itemError) return;

    if (item != null) return;

    if (!pendingRecordingSignupPersist) return;

    if (syncAttempted.current) return;

    syncAttempted.current = true;
    setItem.mutate(
      { key: ENTRY_SOURCE_KEY, value: SOURCE_RECORDING },
      {
        onError: (err) => {
          Sentry.captureException(err, {
            tags: { feature: 'entrySource', phase: 'recording-signup-pending-write' },
          });
        },
        onSuccess: () => {
          clearEntrySource();
        },
      },
    );
  }, [clientStorageEnabled, itemLoading, itemError, item, pendingRecordingSignupPersist, setItem]);

  useEffect(() => {
    if (item?.value === SOURCE_RECORDING && hasLocalRecordingIntent) {
      clearEntrySource();
    }
  }, [item, hasLocalRecordingIntent]);

  if (!isAuthenticated) return hasLocalRecordingIntent ? RESOLVED_RECORDING : RESOLVED_NORMAL;

  if (userQuery.isLoading || !user) return LOADING;

  if (grandfathered) return RESOLVED_NORMAL;

  if (itemLoading) return LOADING;

  if (itemError) return hasLocalRecordingIntent ? RESOLVED_RECORDING : RESOLVED_NORMAL;

  if (item?.value === SOURCE_RECORDING) return RESOLVED_RECORDING;

  if (item == null && hasLocalRecordingIntent) return RESOLVED_RECORDING;

  return RESOLVED_NORMAL;
};
